import { useState, useEffect } from "react";
import { AdminLayout } from "./AdminDashboard";
import API from "../../lib/api";
import { toast } from "sonner";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Trophy, Users, Save, Plus, Trash2, CheckCircle, AlertTriangle, Calendar } from "lucide-react";

export default function AdminLeaderboard() {
  const [data, setData] = useState({});
  const [tab, setTab] = useState("weekly");
  const [weeklyRewards, setWeeklyRewards] = useState([]);
  const [monthlyRewards, setMonthlyRewards] = useState([]);
  const [loading, setLoading] = useState("");

  const load = async () => {
    try {
      const { data: d } = await API.get("/admin/leaderboard");
      setData(d);
      setWeeklyRewards(d.weekly_config?.rewards || []);
      setMonthlyRewards(d.monthly_config?.rewards || []);
    } catch {}
  };
  useEffect(() => { load(); }, []);

  const saveWeekly = async () => {
    setLoading("weekly");
    try { await API.put("/admin/leaderboard/weekly-config", { rewards: weeklyRewards, top_n: weeklyRewards.length, auto_reset: !!data.weekly_config?.auto_reset }); toast.success("Saved"); load(); } catch { toast.error("Failed"); }
    finally { setLoading(""); }
  };
  const saveMonthly = async () => {
    setLoading("monthly");
    try { await API.put("/admin/leaderboard/monthly-config", { rewards: monthlyRewards, top_n: monthlyRewards.length, auto_reset: !!data.monthly_config?.auto_reset }); toast.success("Saved"); load(); } catch { toast.error("Failed"); }
    finally { setLoading(""); }
  };
  const toggleAutoReset = async (kind) => {
    const key = kind === "weekly" ? "weekly_config" : "monthly_config";
    const next = !data[key]?.auto_reset;
    setData(prev => ({ ...prev, [key]: { ...(prev[key] || {}), auto_reset: next } }));
    try {
      await API.put(`/admin/leaderboard/${kind}-config`, { auto_reset: next });
      toast.success(`Auto-reset ${next ? "enabled" : "disabled"}`);
    } catch {
      toast.error("Failed");
      setData(prev => ({ ...prev, [key]: { ...(prev[key] || {}), auto_reset: !next } }));
    }
  };
  const endWeek = async () => {
    if (!window.confirm("End current week? This creates a pending distribution for review.")) return;
    setLoading("end-week");
    try { const { data: d } = await API.post("/admin/leaderboard/end-week", {}); toast.success(d.message); load(); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setLoading(""); }
  };
  const endMonth = async () => {
    if (!window.confirm("End current month? This creates a pending referral distribution.")) return;
    setLoading("end-month");
    try { const { data: d } = await API.post("/admin/leaderboard/end-month", {}); toast.success(d.message); load(); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setLoading(""); }
  };
  const resetCycle = async (kind) => {
    const label = kind === "weekly" ? "weekly coin" : "monthly referral";
    if (!window.confirm(`Wipe the current ${label} rankings WITHOUT paying any prizes? This cannot be undone.`)) return;
    setLoading(`reset-${kind}`);
    try {
      const { data: d } = await API.post(`/admin/leaderboard/reset-${kind}`);
      toast.success(d.message);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setLoading(""); }
  };
  const approveDist = async (distId) => {
    if (!window.confirm("Approve and credit all prizes?")) return;
    setLoading(`approve-${distId}`);
    try { const { data: d } = await API.post(`/admin/leaderboard/approve/${distId}`, {}); toast.success(d.message); load(); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setLoading(""); }
  };

  const RewardEditor = ({ rewards, setRewards }) => (
    <div className="space-y-1">
      {rewards.map((r, i) => (
        <div key={i} className="flex items-center gap-2">
          <span className="text-xs font-mono-game text-[#94A3B8] w-8">#{r.rank || i + 1}</span>
          <Input type="number" value={r.coins} onChange={e => { const nr = [...rewards]; nr[i] = {...nr[i], coins: parseInt(e.target.value) || 0}; setRewards(nr); }}
            className="bg-[#0B0E14] border-[#2A3241] text-[#FFD600] h-7 w-24 text-xs font-mono-game" />
          <span className="text-xs text-[#64748B]">coins</span>
          <button onClick={() => setRewards(rewards.filter((_, j) => j !== i))} className="p-1 text-[#64748B] hover:text-[#EF4444]"><Trash2 className="w-3 h-3" /></button>
        </div>
      ))}
      <button onClick={() => setRewards([...rewards, { rank: rewards.length + 1, coins: 10 }])} className="text-xs text-[#00F0FF] hover:underline flex items-center gap-1 mt-1"><Plus className="w-3 h-3" /> Add rank</button>
    </div>
  );

  return (
    <AdminLayout title="Leaderboard Management">
      <div className="space-y-4" data-testid="admin-leaderboard">
        <div className="flex gap-1 bg-[#131722] rounded-lg p-1 border border-[#2A3241]/40">
          {[["weekly", "Weekly Coins"], ["monthly", "Monthly Referrals"], ["pending", "Pending Approvals"], ["archive", "Archive"]].map(([k, l]) => (
            <button key={k} onClick={() => setTab(k)} className={`flex-1 py-2 text-xs font-bold uppercase tracking-widest rounded-md ${tab === k ? "bg-[#FFD600]/10 text-[#FFD600]" : "text-[#64748B]"}`}>{l}</button>
          ))}
        </div>

        {/* WEEKLY COINS */}
        {tab === "weekly" && (
          <div className="space-y-4">
            <div className="bg-[#131722] rounded-xl border border-[#2A3241]/40 p-5 space-y-3">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-2"><Trophy className="w-5 h-5 text-[#FFD600]" /><h3 className="text-sm font-bold text-[#F8FAFC]">Weekly Prize Configuration</h3></div>
                <div className="flex gap-2">
                  <button onClick={() => resetCycle("weekly")} disabled={!!loading} data-testid="reset-weekly-btn"
                    className="px-3 py-2 text-xs font-bold uppercase bg-[#64748B]/10 text-[#94A3B8] border border-[#64748B]/30 rounded-lg hover:bg-[#64748B]/20 disabled:opacity-50">Reset (No Payout)</button>
                  <button onClick={endWeek} disabled={!!loading} className="px-4 py-2 text-xs font-bold uppercase bg-[#EF4444]/20 text-[#EF4444] border border-[#EF4444]/30 rounded-lg hover:bg-[#EF4444]/30 disabled:opacity-50">End Week</button>
                </div>
              </div>
              <div className="flex items-center gap-3 px-3 py-2 bg-[#0B0E14] rounded-lg border border-[#2A3241]/40">
                <button onClick={() => toggleAutoReset("weekly")} data-testid="weekly-auto-reset-toggle"
                  className={`relative w-10 h-5 rounded-full transition-colors ${data.weekly_config?.auto_reset ? "bg-[#10B981]" : "bg-[#2A3241]"}`}>
                  <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full transition-transform ${data.weekly_config?.auto_reset ? "translate-x-5" : "translate-x-0.5"}`} />
                </button>
                <div className="flex-1">
                  <p className="text-xs font-bold text-[#F8FAFC]">Auto-reset every Sunday 23:00 UTC</p>
                  <p className="text-[10px] text-[#64748B]">When ON, the system automatically closes the week and queues a pending distribution for your approval.</p>
                </div>
              </div>
              <RewardEditor rewards={weeklyRewards} setRewards={setWeeklyRewards} />
              <button onClick={saveWeekly} disabled={loading === "weekly"} className="flex items-center gap-2 px-4 py-2 text-xs font-bold uppercase bg-[#FFD600] text-[#0B0E14] rounded-lg disabled:opacity-50"><Save className="w-3 h-3" /> Save Weekly Config</button>
            </div>
            <div className="bg-[#131722] rounded-xl border border-[#2A3241]/40 p-5">
              <h3 className="text-sm font-bold text-[#F8FAFC] mb-3">Current Week Rankings ({(data.weekly_rankings || []).length} users)</h3>
              <div className="space-y-1 max-h-64 overflow-y-auto">
                {(data.weekly_rankings || []).map((r, i) => (
                  <div key={r.user_id || `r-${i}`} className="flex items-center justify-between py-1.5 border-b border-[#2A3241]/20">
                    <div className="flex items-center gap-3"><span className="font-mono-game text-xs w-6 text-[#94A3B8]">#{i + 1}</span><span className="text-sm text-[#F8FAFC]">{r.name}</span></div>
                    <span className="font-mono-game text-xs text-[#FFD600]">{r.coins_earned?.toFixed(2)}</span>
                  </div>
                ))}
                {!(data.weekly_rankings || []).length && <p className="text-xs text-[#64748B]">No activity this week yet</p>}
              </div>
            </div>
          </div>
        )}

        {/* MONTHLY REFERRALS */}
        {tab === "monthly" && (
          <div className="space-y-4">
            <div className="bg-[#131722] rounded-xl border border-[#2A3241]/40 p-5 space-y-3">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-2"><Users className="w-5 h-5 text-[#B026FF]" /><h3 className="text-sm font-bold text-[#F8FAFC]">Monthly Referral Prize Configuration</h3></div>
                <div className="flex gap-2">
                  <button onClick={() => resetCycle("monthly")} disabled={!!loading} data-testid="reset-monthly-btn"
                    className="px-3 py-2 text-xs font-bold uppercase bg-[#64748B]/10 text-[#94A3B8] border border-[#64748B]/30 rounded-lg hover:bg-[#64748B]/20 disabled:opacity-50">Reset (No Payout)</button>
                  <button onClick={endMonth} disabled={!!loading} className="px-4 py-2 text-xs font-bold uppercase bg-[#EF4444]/20 text-[#EF4444] border border-[#EF4444]/30 rounded-lg hover:bg-[#EF4444]/30 disabled:opacity-50">End Month</button>
                </div>
              </div>
              <div className="flex items-center gap-3 px-3 py-2 bg-[#0B0E14] rounded-lg border border-[#2A3241]/40">
                <button onClick={() => toggleAutoReset("monthly")} data-testid="monthly-auto-reset-toggle"
                  className={`relative w-10 h-5 rounded-full transition-colors ${data.monthly_config?.auto_reset ? "bg-[#10B981]" : "bg-[#2A3241]"}`}>
                  <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full transition-transform ${data.monthly_config?.auto_reset ? "translate-x-5" : "translate-x-0.5"}`} />
                </button>
                <div className="flex-1">
                  <p className="text-xs font-bold text-[#F8FAFC]">Auto-reset on last day of month at 23:00 UTC</p>
                  <p className="text-[10px] text-[#64748B]">When ON, the system automatically closes the month and queues a pending distribution for your approval.</p>
                </div>
              </div>
              <RewardEditor rewards={monthlyRewards} setRewards={setMonthlyRewards} />
              <button onClick={saveMonthly} disabled={loading === "monthly"} className="flex items-center gap-2 px-4 py-2 text-xs font-bold uppercase bg-[#B026FF] text-white rounded-lg disabled:opacity-50"><Save className="w-3 h-3" /> Save Monthly Config</button>
            </div>
            <div className="bg-[#131722] rounded-xl border border-[#2A3241]/40 p-5">
              <h3 className="text-sm font-bold text-[#F8FAFC] mb-3">Current Month Referrals ({(data.monthly_referrals || []).length} users)</h3>
              <div className="space-y-1 max-h-64 overflow-y-auto">
                {(data.monthly_referrals || []).map((r, i) => (
                  <div key={r.user_id || `r-${i}`} className="flex items-center justify-between py-1.5 border-b border-[#2A3241]/20">
                    <div className="flex items-center gap-3"><span className="font-mono-game text-xs w-6 text-[#94A3B8]">#{i + 1}</span><span className="text-sm text-[#F8FAFC]">{r.name}</span></div>
                    <span className="font-mono-game text-xs text-[#B026FF]">{r.referrals} refs</span>
                  </div>
                ))}
                {!(data.monthly_referrals || []).length && <p className="text-xs text-[#64748B]">No referrals this month yet</p>}
              </div>
            </div>
          </div>
        )}

        {/* PENDING APPROVALS */}
        {tab === "pending" && (
          <div className="space-y-3">
            <p className="text-sm text-[#94A3B8]">Review rankings before approving prizes. Replace cheaters if needed.</p>
            {(data.pending || []).filter(d => d.status === "pending").map(dist => (
              <div key={dist.dist_id} className="bg-[#131722] rounded-xl border border-[#F59E0B]/20 p-5" data-testid={`pending-${dist.dist_id}`}>
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <p className="text-sm font-bold text-[#F8FAFC]">{dist.label}</p>
                    <p className="text-xs text-[#64748B]">{dist.type} | {dist.entries?.length || 0} entries | {new Date(dist.created_at).toLocaleDateString()}</p>
                  </div>
                  <button onClick={() => approveDist(dist.dist_id)} disabled={!!loading} className="flex items-center gap-1 px-4 py-2 text-xs font-bold uppercase bg-[#10B981] text-white rounded-lg disabled:opacity-50"><CheckCircle className="w-3 h-3" /> Approve All</button>
                </div>
                <div className="space-y-1 max-h-48 overflow-y-auto">
                  {(dist.entries || []).filter(e => e.prize > 0).map((e, i) => (
                    <div key={i} className="flex items-center justify-between py-1 border-b border-[#2A3241]/20 text-xs">
                      <div className="flex items-center gap-2"><span className="font-mono-game text-[#94A3B8] w-6">#{e.rank}</span><span className="text-[#F8FAFC]">{e.name}</span></div>
                      <div className="flex items-center gap-3">
                        <span className="text-[#94A3B8]">{e.coins_earned?.toFixed(2) || e.referrals || 0}</span>
                        <span className="font-mono-game text-[#FFD600]">+{e.prize}c</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
            {!(data.pending || []).filter(d => d.status === "pending").length && <p className="text-[#94A3B8] text-center py-6">No pending distributions</p>}
          </div>
        )}

        {/* ARCHIVE */}
        {tab === "archive" && (
          <div className="bg-[#131722] rounded-xl border border-[#2A3241]/40 p-5">
            <h3 className="text-sm font-bold text-[#F8FAFC] mb-3 flex items-center gap-2"><Calendar className="w-4 h-4 text-[#00F0FF]" /> Past Winners Archive</h3>
            {(data.archive || []).length > 0 ? (
              <div className="space-y-1">
                {(data.archive || []).map((a, i) => (
                  <div key={a.label + a.type + i} className="flex items-center justify-between py-2 border-b border-[#2A3241]/20">
                    <div className="flex items-center gap-3">
                      <span className={`text-[10px] px-2 py-0.5 rounded font-bold ${a.type === "weekly_coins" ? "bg-[#FFD600]/10 text-[#FFD600]" : "bg-[#B026FF]/10 text-[#B026FF]"}`}>
                        {a.type === "weekly_coins" ? "WEEKLY" : "MONTHLY"}
                      </span>
                      <span className="text-sm text-[#F8FAFC]">{a.winner_name}</span>
                    </div>
                    <div className="text-right">
                      <p className="font-mono-game text-xs text-[#FFD600]">+{a.winner_prize}c</p>
                      <p className="text-[10px] text-[#64748B]">{a.label} | {a.winner_value} {a.type === "weekly_coins" ? "earned" : "refs"}</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : <p className="text-xs text-[#64748B]">No archive entries yet. Complete a week/month to see winners here.</p>}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
